<template>
  <section
    class="invoice-preview-wrapper"
    aria-label="Détails du bon de transfert"
    role="main"
  >

    <div
      v-if="isLoading === true"
      class="loading-container d-print-none"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner
        variant="primary"
        class="loading-spinner"
      />
      <p class="mt-3 text-muted">
        Chargement du bon de transfert...
      </p>
    </div>

    <!-- Main Content -->
    <div v-if="transferVoucher">
      <!-- Top Action Bar -->
      <div class="top-action-bar mb-4 d-print-none">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            md="6"
          >
            <h2 class="page-title mb-0">
              <b-icon
                icon="box-seam"
                class="mr-2"
              />
              Bon de Transfert N° {{ transferVoucher.id }}
            </h2>
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="text-md-right"
          >
            <div class="action-buttons-top">
              <b-dropdown
                v-ripple.400="'rgba(229, 62, 62, 0.15)'"
                variant="outline-danger"
                class="action-btn-top mr-2"
                :disabled="isLoading"
                text="Imprimer"
                right
              >
                <template #button-content>
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Imprimer
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="14"
                    class="ml-1"
                  />
                </template>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer une version simplifiée en noir et blanc'"
                  @click="printTransferVoucherBW"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version noir et blanc
                </b-dropdown-item>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer le bon de transfert en couleur avec tous les éléments graphiques.'"
                  @click="printTransferVoucher"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version couleur
                </b-dropdown-item>
              </b-dropdown>
              <b-button
                variant="outline-secondary"
                class="action-btn-top"
                @click="$router.go(-1)"
              >
                <b-icon
                  icon="arrow-left"
                  class="mr-1"
                />
                Retour
              </b-button>
              <div class="quick-stats d-inline-flex ml-3">
                <div class="stat-item-inline success">
                  <b-icon
                    icon="box-seam"
                    size="14"
                  />
                  <span>{{ (transferVoucher.orders && transferVoucher.orders.length) || 0 }}</span>
                </div>
                <div class="stat-item-inline warning">
                  <b-icon
                    icon="arrow-left-right"
                    size="14"
                  />
                  <span>{{ exchangeCount }}</span>
                </div>
                <div class="stat-item-inline primary">
                  <b-icon
                    icon="cash-stack"
                    size="14"
                  />
                  <span>{{ (transferVoucher.total || 0).toFixed(3) }} DT</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Voucher Content (Print Area) -->
      <div
        id="voucher-content"
        class="invoice-content"
      >
        <b-card class="invoice-card shadow-sm">
          <!-- Header Section -->
          <div class="invoice-header">
            <b-row class="align-items-center">
              <b-col
                cols="12"
                md="6"
                class="mb-3 mb-md-0"
              >
                <div class="company-logo">
                  <img
                    src="@/assets/images/logo/logo.svg"
                    :alt="$branding.logo.alt"
                    class="logo-img"
                  >
                </div>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="text-md-right"
              >
                <h1 class="invoice-title">
                  Bon de Transfert
                  <span class="invoice-number">N° {{ transferVoucher.id }}</span>
                </h1>
                <div class="invoice-date">
                  <b-icon
                    icon="calendar3"
                    class="mr-1"
                  />
                  {{ formatFn(transferVoucher.created_at) }}
                </div>
                <div class="invoice-date">
                  <b-icon
                    icon="geo-alt-fill"
                    class="mr-1"
                  />
                  {{ (transferVoucher.from_repository && transferVoucher.from_repository.name) || 'N/A' }}
                  <b-icon
                    icon="arrow-right"
                    class="mx-2 text-primary"
                    font-scale="1.2"
                  />
                  {{ (transferVoucher.to_repository && transferVoucher.to_repository.name) || 'N/A' }}
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Company & Transport Info -->
          <b-row class="company-info-section">
            <b-col
              cols="12"
              md="6"
            >
              <document-company-info
                :company="company"
                header-title="Notre Société"
                layout="vertical"
              />
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <div class="info-card supplier-card">
                <div class="info-header">
                  <b-icon
                    icon="truck"
                    class="mr-2"
                  />
                  <h5 class="mb-0">
                    Transport
                  </h5>
                </div>
                <div class="info-content">
                  <div class="info-item">
                    <span class="info-label">Chauffeur :</span>
                    <span>{{ (transferVoucher.delivery_man && transferVoucher.delivery_man.username) || 'Non assigné' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Véhicule :</span>
                    <span>{{ (transferVoucher.car && transferVoucher.car.serial_number) || 'Non assigné' }}</span>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Summary Table -->
          <div class="summary-section">
            <h5 class="section-title">
              <b-icon
                icon="bar-chart"
                class="mr-2"
              />
              Résumé du bon de transfert
            </h5>
            <div class="summary-table-container">
              <div class="table-responsive">
                <table class="modern-table">
                  <thead>
                    <tr>
                      <th>Colis total</th>
                      <th>Échanges</th>
                      <th>Total TTC</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ (transferVoucher.orders && transferVoucher.orders.length) || 0 }}</td>
                      <td>{{ exchangeCount }}</td>
                      <td>{{ (transferVoucher.total || 0).toFixed(3) }} DT</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Orders Table -->
          <div
            v-if="orders.length"
            class="orders-section"
          >
            <div class="section-header">
              <h5 class="section-title">
                <b-icon
                  icon="list-check"
                  class="mr-2"
                />
                Détail des Colis
              </h5>
            </div>
            <div class="table-container">
              <b-table
                :items="orders"
                :fields="tableFields"
                class="modern-orders-table"
                responsive
                striped
                hover
                small
                :tbody-tr-class="getRowClass"
              >
                <template #cell(BL)="{ item }">
                  <div class="qr-cell">
                    <qrcode-vue
                      :value="item.BL.toString()"
                      :size="35"
                      level="H"
                      class="qr-code"
                    />
                    <div class="ref-info">
                      <span class="ref-number">{{ item.BL }}</span>
                      <div class="badges-container">
                        <b-badge
                          v-if="item.status === 18"
                          variant="warning"
                          class="status-badge"
                        >
                          {{ item.status_display || 'Planification' }}
                        </b-badge>
                        <b-badge
                          v-if="item.is_an_exchange"
                          variant="danger"
                          class="exchange-badge"
                        >
                          Échange
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </template>
                <template #cell(prix)="{ item }">
                  <span class="price-cell font-weight-bold text-success">
                    {{ parseFloat(item.prix).toFixed(3) }} DT
                  </span>
                </template>
                <template #cell(tel)="{ item }">
                  <a
                    :href="`tel:${item.tel}`"
                    class="phone-link"
                  >
                    <b-icon
                      icon="telephone"
                      class="mr-1"
                    />
                    {{ item.tel }}
                  </a>
                </template>
              </b-table>
            </div>
          </div>

          <!-- Signature -->
          <div class="summary-section mt-4">
            <h5 class="section-title">
              <b-icon
                icon="pen"
                class="mr-2"
              />
              Signature du Responsable
            </h5>
            <div class="signature-box" />
          </div>
        </b-card>
      </div>
    </div>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BButton,
  BBadge,
  BSpinner,
  BIcon,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'

import axios from 'axios'
import storeAuth from '@/store/store'
import QrcodeVue from 'qrcode.vue'
import { DocumentCompanyInfo } from '@/components/document'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BIcon,
    QrcodeVue,
    BDropdown,
    BDropdownItem,
    DocumentCompanyInfo,
  },
  data() {
    return {
      transferVoucher: {},
      orders: [],
      isLoading: true,
      company: {},
      tableFields: [
        {
          key: 'BL',
          label: 'BL',
          thClass: 'text-center border-0',
          tdClass: 'text-center border-0',
          sortable: false,
        },
        {
          key: 'fournisseur',
          label: 'Fournisseur',
          thClass: 'border-0',
          tdClass: 'border-0',
        },
        {
          key: 'destinataire',
          label: 'Destinataire',
          thClass: 'border-0',
          tdClass: 'border-0',
        },
        {
          key: 'gouvernorat',
          label: 'Gouvernorat',
          thClass: 'border-0',
          tdClass: 'border-0',
        },
        {
          key: 'tel',
          label: 'Téléphone',
          thClass: 'border-0',
          tdClass: 'border-0',
        },
        {
          key: 'prix',
          label: 'Prix TTC',
          thClass: 'text-right border-0',
          tdClass: 'text-right border-0',
        },
      ],
    }
  },

  computed: {
    exchangeCount() {
      return this.orders.filter(order => order.is_an_exchange).length
    },
  },

  created() {
    this.id = this.$route.params.id
    this.company = storeAuth.state.company
    this.getTransferVoucher()
  },

  methods: {
    formatFn(date) {
      if (!date) return 'N/A'
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const time = date.slice(11, 19)
      return `${day1}/${month}/${year} à ${time}`
    },

    async getTransferVoucher() {
      try {
        this.isLoading = true
        const response = await axios.get(`/api/transfer-vouchers/detail/${this.$route.params.id}`)
        this.transferVoucher = response.data

        if (this.transferVoucher && this.transferVoucher.orders) {
          this.orders = this.transferVoucher.orders.map(order => ({
            BL: order.id,
            gouvernorat: (order.governorate && order.governorate.name) || 'N/A',
            fournisseur: (order.supplier && order.supplier.username) || 'N/A',
            tel: order.phone1 || 'N/A',
            destinataire: order.name || 'N/A',
            adresse: order.address || 'N/A',
            prix: order.price_ttc || 0,
            is_an_exchange: order.is_an_exchange,
            status: order.status,
            status_display: order.status_display,
          }))
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error)
        this.transferVoucher = undefined
      } finally {
        this.isLoading = false
      }
    },

    getRowClass(item) {
      return item.status === 18 ? 'table-warning-custom' : ''
    },

    printTransferVoucher() {
      // Dynamic print CSS injection (same as invoices/Show.vue)
      const printCSS = `
        <style id="print-styles-transfer-voucher">
          @media print {
            html, body, #app {
              margin: 0 !important;
              padding: 0 !important;
              font-family: Arial, sans-serif;
              position: static !important;
              width: 100% !important;
              overflow: visible !important;
              background: white !important;
              box-sizing: border-box !important;
            }
            .invoice-preview-wrapper, .transfer-voucher-wrapper, section.invoice-preview-wrapper {
              position: static !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              background: white !important;
              box-sizing: border-box !important;
            }
            body * { visibility: hidden; }
            #voucher-content, #voucher-content * { visibility: visible; }
            #voucher-content {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              right: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              font-family: Arial, sans-serif;
              font-size: 14px;
              line-height: 1.4;
              color: black;
              background: white !important;
              z-index: 9999 !important;
              user-select: none !important;
              cursor: default !important;
            }
            .modern-table, .modern-orders-table {
              user-select: none !important;
              cursor: default !important;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            .invoice-header {
              margin-bottom: 20px;
              padding: 20px;
              background: #E53E3E !important;
              color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              page-break-inside: avoid;
            }
            .logo-img {
              max-width: 120px !important;
              height: auto !important;
              filter: brightness(0) invert(1) !important;
            }
            .invoice-title {
              font-size: 24px;
              font-weight: bold;
              color: white !important;
              margin-bottom: 8px;
            }
            .invoice-number {
              color: #fed7d7 !important;
            }
            .invoice-date {
              font-size: 14px;
              color: white !important;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              border-bottom: 2px solid black;
              margin: 15px 0 10px 0;
              padding-bottom: 5px;
              color: black !important;
              page-break-after: avoid;
            }
            .row { display: flex; flex-wrap: wrap; margin: 0; }
            .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 5px; }
            .info-card {
              border: 1px solid black;
              padding: 15px;
              margin-bottom: 15px;
              background: white !important;
              border-left: 4px solid #E53E3E !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              min-height: 120px;
              page-break-inside: avoid;
            }
            .supplier-card {
              border-left-color: #2D3748 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              min-height: 120px;
            }
            .info-header {
              font-size: 14px;
              font-weight: bold;
              color: black !important;
              margin-bottom: 10px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 5px;
            }
            .info-item {
              margin-bottom: 8px;
              font-size: 14px;
              color: black !important;
            }
            .info-label {
              font-weight: bold;
              color: black !important;
              min-width: 100px;
              display: inline-block;
              font-size: 14px;
            }
            .info-subtitle {
              font-style: italic;
              color: #666 !important;
              font-size: 13px;
            }
            .summary-table-container {
              border: 1px solid black;
              margin-bottom: 20px;
              background: white !important;
            }
            .table-container {
              border: 1px solid black;
              margin-bottom: 20px;
              background: white !important;
            }
            .modern-table, .modern-orders-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            .modern-table th {
              background: #E53E3E !important;
              color: white !important;
              padding: 8px 6px;
              border: 1px solid black;
              font-weight: bold;
              font-size: 14px;
              text-align: center;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .modern-table td, .modern-orders-table td {
              padding: 6px 4px;
              border: 1px solid black;
              color: black !important;
              font-size: 14px;
              text-align: center;
            }
            /* Removed page-break-inside: avoid from table rows to allow natural page breaks */
            .modern-orders-table th {
              background: #f0f0f0 !important;
              color: black !important;
              padding: 6px 4px;
              border: 1px solid black;
              font-weight: bold;
              font-size: 14px;
              text-align: left;
            }
            .modern-orders-table tbody tr {
              page-break-inside: avoid;
            }
            .stat-badge {
              background: none !important;
              color: black !important;
              border: 1px solid black !important;
              padding: 2px 6px;
              font-size: 13px;
              font-weight: bold;
            }
            .amount {
              font-weight: bold;
              color: black !important;
              font-size: 13px;
            }
            .total-amount {
              font-weight: bold;
              color: #E53E3E !important;
              font-size: 14px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .signature-section, .company-info-section {
              page-break-inside: avoid;
            }
            .table-header, .voucher-header, .card-header-custom, .signature-header {
              page-break-after: avoid !important;
            }
            .reason-item {
              page-break-inside: avoid !important;
            }
            .signature-title {
              font-weight: bold;
              color: black !important;
              margin-bottom: 15px;
              font-size: 12px;
              text-align: center;
            }
            .signature-line {
              height: 60px;
              border-bottom: 2px solid black;
              margin-bottom: 5px;
              background: white !important;
            }
            .ml-2, .badge:not(.stat-badge) {
              display: none !important;
            }
            .section-header {
              page-break-after: avoid;
            }
            * {
              transition: none !important;
              animation: none !important;
              transform: none !important;
            }
          }
        </style>
      `
      document.head.insertAdjacentHTML('beforeend', printCSS)
      window.print()
      setTimeout(() => {
        const printStyleElement = document.getElementById('print-styles-transfer-voucher')
        if (printStyleElement) {
          printStyleElement.remove()
        }
      }, 1000)
    },
    printTransferVoucherBW() {
      // Ultra-optimized print version: no backgrounds, only black text, minimal borders, visible links, hide scrollbars
      const printCSS = `
        <style id="print-styles-transfer-voucher-bw">
          @media print {
            html, body, #app {
              margin: 0 !important;
              padding: 0 !important;
              font-family: Arial, sans-serif;
              position: static !important;
              width: 100% !important;
              overflow: visible !important;
              background: #fff !important;
              color: #000 !important;
              box-sizing: border-box !important;
            }
            .invoice-preview-wrapper, .transfer-voucher-wrapper, section.invoice-preview-wrapper {
              position: static !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              background: #fff !important;
              color: #000 !important;
              box-sizing: border-box !important;
            }
            /* Hide scrollbars for all elements in print */
            *::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              background: transparent !important;
            }
            * {
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            body * { visibility: hidden; }
            #voucher-content, #voucher-content * { visibility: visible; }
            #voucher-content {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              right: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              font-family: Arial, sans-serif;
              font-size: 14px;
              line-height: 1.4;
              color: #000 !important;
              background: #fff !important;
              z-index: 9999 !important;
              user-select: none !important;
              cursor: default !important;
            }
            .modern-table, .modern-orders-table {
              user-select: none !important;
              cursor: default !important;
              background: #fff !important;
              color: #000 !important;
              border: 1px solid #bbb !important;
            }
            .modern-table th, .modern-orders-table th {
              background: none !important;
              color: #000 !important;
              border: 1px solid #bbb !important;
              font-weight: bold;
              font-size: 14px;
              text-align: center;
              padding: 8px 6px;
            }
            .modern-table td, .modern-orders-table td {
              background: none !important;
              color: #000 !important;
              border: 1px solid #bbb !important;
              font-size: 14px;
              text-align: center;
              padding: 6px 4px;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            .invoice-header {
              margin-bottom: 20px;
              padding: 20px 0 10px 0;
              background: none !important;
              color: #000 !important;
              border-bottom: 2px solid #bbb !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              page-break-inside: avoid;
            }
            .logo-img {
              max-width: 120px !important;
              height: auto !important;
              filter: none !important;
            }
            .invoice-title {
              font-size: 24px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 8px;
            }
            .invoice-number {
              color: #000 !important;
            }
            .invoice-date {
              font-size: 14px;
              color: #000 !important;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              border-bottom: 2px solid #bbb !important;
              margin: 15px 0 10px 0;
              padding-bottom: 5px;
              color: #000 !important;
              page-break-after: avoid;
            }
            .row { display: flex; flex-wrap: wrap; margin: 0; }
            .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 5px; }
            .info-card, .supplier-card {
              border: 1px solid #bbb !important;
              padding: 15px;
              margin-bottom: 15px;
              background: none !important;
              border-left: none !important;
              min-height: 120px;
              page-break-inside: avoid;
              color: #000 !important;
            }
            .info-header {
              font-size: 14px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 10px;
              border-bottom: 1px solid #bbb !important;
              padding-bottom: 5px;
            }
            .info-item {
              margin-bottom: 8px;
              font-size: 14px;
              color: #000 !important;
            }
            .info-label {
              font-weight: bold;
              color: #000 !important;
              min-width: 100px;
              display: inline-block;
              font-size: 14px;
            }
            .info-subtitle {
              font-style: italic;
              color: #555 !important;
              font-size: 13px;
            }
            .summary-table-container, .table-container {
              border: 1px solid #bbb !important;
              margin-bottom: 20px;
              background: none !important;
              color: #000 !important;
            }
            .stat-badge, .amount, .total-amount {
              background: none !important;
              color: #000 !important;
              border: none !important;
              font-weight: bold;
            }
            .signature-section, .company-info-section {
              page-break-inside: avoid;
            }
            .table-header, .voucher-header, .card-header-custom, .signature-header {
              page-break-after: avoid !important;
            }
            .reason-item {
              page-break-inside: avoid !important;
            }
            .signature-title {
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 15px;
              font-size: 12px;
              text-align: center;
            }
            .signature-line {
              height: 60px;
              border-bottom: 2px solid #000 !important;
              margin-bottom: 5px;
              background: #fff !important;
            }
            .ml-2, .badge:not(.stat-badge) {
              display: none !important;
            }
            .section-header {
              page-break-after: avoid;
            }
            a, .phone-link {
              color: #000 !important;
              text-decoration: underline !important;
              font-weight: bold !important;
              word-break: break-all;
            }
            a[href]:after, .phone-link[href]:after {
              content: " (" attr(href) ")";
              font-size: 11px;
              color: #555;
            }
            * {
              transition: none !important;
              animation: none !important;
              transform: none !important;
            }
          }
        </style>
      `
      document.head.insertAdjacentHTML('beforeend', printCSS)
      window.print()
      setTimeout(() => {
        const printStyleElement = document.getElementById('print-styles-transfer-voucher-bw')
        if (printStyleElement) {
          printStyleElement.remove()
        }
      }, 1000)
    },
  },
}
</script>

<style lang="scss" scoped>
// Brand Colors - B2C Delivery
$brand-primary: #E53E3E;
$brand-secondary: #2D3748;
$brand-light: #FED7D7;
$brand-dark: #1A202C;

.invoice-preview-wrapper {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  min-height: 100vh;
  font-size: 16px;
}

.top-action-bar {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-left: 4px solid $brand-primary;
}

.page-title {
  color: $brand-secondary;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.action-buttons-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 1rem;
  }
}

.action-btn-top {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border-color: $brand-primary;
  color: $brand-primary;
  &:hover {
    background-color: rgba(229, 62, 62, 0.1);
    border-color: $brand-primary;
    color: $brand-primary;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(229, 62, 62, 0.2);
  }
}

.quick-stats {
  @media (max-width: 768px) {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
}

.stat-item-inline {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  margin-left: 0.5rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  svg, .b-icon {
    margin-right: 0.25rem;
  }
  &.success {
    background: rgba(72, 187, 120, 0.1);
    color: #48bb78;
  }
  &.warning {
    background: rgba(237, 137, 54, 0.1);
    color: #ed8936;
  }
  &.primary {
    background: rgba(229, 62, 62, 0.1);
    color: $brand-primary;
  }
}

.modern-alert {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.15);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
}

.invoice-content {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.invoice-card {
  border-radius: 16px;
  border: none;
  overflow: hidden;
  background: #fff;
}

.invoice-header {
  padding: 2rem;
  background: linear-gradient(135deg, $brand-primary 0%, #c53030 100%);
  color: white;
  margin: -1.25rem -1.25rem 2rem -1.25rem;
}

.company-logo {
  .logo-img {
    max-width: 200px;
    height: auto;
  }
}

.invoice-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.invoice-number {
  color: #fed7d7;
}

.invoice-date {
  font-size: 1rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.company-info-section {
  margin-bottom: 2rem;
}

.info-card {
  background: #f7fafc;
  border-radius: 12px;
  padding: 1.5rem;
  height: 100%;
  border-left: 4px solid $brand-primary;
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
  }
}

.supplier-card {
  border-left-color: $brand-secondary;
}

.info-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: $brand-secondary;
  font-weight: 600;
}

.info-content {
  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f8f9fa;
    &:last-child {
      border-bottom: none;
    }
    .info-label {
      font-weight: 600;
      color: #6c757d;
      font-size: 0.9rem;
    }
    .info-value {
      font-weight: 500;
      color: #495057;
      text-align: right;
      max-width: 60%;
    }
    .info-subtitle {
      font-size: 0.85rem;
      color: #888;
      margin-left: 0.5rem;
    }
  }
}

.summary-section, .orders-section {
  margin-bottom: 2rem;
}

.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: $brand-secondary;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.section-header {
  margin-bottom: 1rem;
  font-size: 1.15rem;
}

.summary-table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.modern-table {
  width: 100%;
  margin-bottom: 0;
  thead {
    background: #f8f9fa;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.5px;
    color: #495057;
  }
  th, td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    border-color: #f1f3f4;
    text-align: center;
  }
}

.table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.modern-orders-table {
  margin-bottom: 0;
}

.qr-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  .qr-code {
    flex-shrink: 0;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  .ref-info {
    .ref-number {
      font-weight: bold;
      color: #495057;
      display: block;
    }
    .badges-container {
      margin-top: 0.25rem;
      .status-badge, .exchange-badge {
        font-size: 0.7rem;
        margin-right: 0.25rem;
      }
    }
  }
}

.price-cell {
  font-size: 1.05rem;
}

.phone-link {
  color: #007bff;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.signature-box {
  height: 80px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #fafafa;
}

// Print styles for transfer voucher
// Unified global print CSS to prevent blank/black page and blank first page
@media print {
  html, body, #app, .invoice-preview-wrapper, .transfer-voucher-wrapper {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    font-size: 12px;
    width: 100% !important;
    position: static !important;
    box-sizing: border-box !important;
  }
  .invoice-preview-wrapper, .transfer-voucher-wrapper {
    position: static !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  main, section, .invoice-content, .invoice-card {
    margin: 0 !important;
    padding: 0 !important;
    position: static !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  .top-action-bar, .d-print-none {
    display: none !important;
  }
  // Hide navigation and non-essential elements
  nav, .main-menu, .header-navbar-shadow,
  .customizer-toggle, footer.footer,
  .actions-card, .loading-container, .error-container {
    display: none !important;
  }
  .content.app-content {
    margin: 0 !important;
    padding: 1rem !important;
  }
  .invoice-card, .card {
    box-shadow: none !important;
    border: 1px solid #dee2e6 !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .modern-table, .modern-orders-table {
    font-size: 11px;
    th, td {
      padding: 0.5rem !important;
      border: 1px solid #000 !important;
    }
    tr {
      page-break-inside: avoid !important;
    }
    .qr-cell {
      flex-direction: row;
      align-items: center;
    }
    .qr-code {
      width: 30px !important;
      height: 30px !important;
    }
  }
  // Section titles and headers: keep with content
  .table-header,
  .voucher-header,
  .card-header-custom,
  .signature-header {
    page-break-after: avoid !important;
  }
  // Keep logical sections together
  .info-card,
  .stats-card,
  .signature-card {
    page-break-inside: avoid !important;
  }
  // Color adjustments for print
  .bg-primary, .text-primary { color: #000 !important; }
  .bg-success, .text-success { color: #000 !important; }
  .bg-warning, .text-warning { color: #000 !important; }
  // Signature box for print
  .signature-box {
    height: 60px !important;
    border: 2px solid #000 !important;
    background: white !important;
  }
  // Reason items: don't split
  .reason-item {
    page-break-inside: avoid !important;
  }
}
</style>

<style lang="scss">
// Print Styles
@media print {
  html, body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    font-size: 12px;
  }

  .transfer-voucher-wrapper {
    background: white !important;
    padding: 0 !important;
  }

  // Hide navigation and non-essential elements
  nav, .main-menu, .header-navbar-shadow,
  .customizer-toggle, footer.footer,
  .actions-card, .loading-container, .error-container {
    display: none !important;
  }

  .content.app-content {
    margin: 0 !important;
    padding: 1rem !important;
  }

  // Ensure cards print properly
  .card {
    box-shadow: none !important;
    border: 1px solid #dee2e6 !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  // Table print optimization
  .modern-table {
    font-size: 11px;

    th, td {
      padding: 0.5rem !important;
      border: 1px solid #000 !important;
    }

    // Remove page-break-inside: avoid from table container
    // Add page-break-inside: avoid to table rows
    tr {
      page-break-inside: avoid !important;
    }

    .qr-cell {
      flex-direction: row;
      align-items: center;
    }

    .qr-code {
      width: 30px !important;
      height: 30px !important;
    }
  }

  // Section titles and headers: keep with content
  .table-header,
  .voucher-header,
  .card-header-custom,
  .signature-header {
    page-break-after: avoid !important;
  }

  // Keep logical sections together
  .info-card,
  .stats-card,
  .signature-card {
    page-break-inside: avoid !important;
  }

  // Color adjustments for print
  .bg-primary, .text-primary { color: #000 !important; }
  .bg-success, .text-success { color: #000 !important; }
  .bg-warning, .text-warning { color: #000 !important; }

  // Signature box for print
  .signature-box {
    height: 60px !important;
    border: 2px solid #000 !important;
    background: white !important;
  }

  // Reason items: don't split
  .reason-item {
    page-break-inside: avoid !important;
  }
}
</style>
